import Box from "@/components/box/box";
import { showTranslated, translate } from "@/config/translator";
import {
  Image,
  Persona,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
} from "@fluentui/react-components";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Calendar from "react-calendar";
import { CalendarDetailsText } from "./calendarDetailsText";
import { onDownloadCircularLetter } from "./homeUtils";
import {
  formatDateFromLang,
  formatPhoneNumber,
  getFileFAIcon,
} from "@/config/utils";
import TextButton from "@/components/textButton/TextButton";
import { FullScreenSpinner } from "@/components/fullScreenSpinner/FullScreenSpinner";
import {
  FATTURE_IN_CLOUD_URL,
  TEAM_SYSTEM_DIGITAL_URL,
  TEAM_SYSTEM_STUDIO_HR_URL,
  PSP_YOUTUBE_CHANNEL_URL,
} from "@/config/constants";
import tsLogo from "@/assets/ts_logo.png";
import ytLogo from "@/assets/yt_logo.png";
import { useHomeDesktop } from "./homeDesktop/useHomeDesktop";

export default function HomeDesktop() {
  const {
    lang,
    isLoading,
    tileContentFuncWrapper,
    selectedCalendarEntries,
    selectedDate,
    circularLettersColumns,
    circularLetters,
    messagesColumns,
    messages,
    selectedMandate,
    navigate,
  } = useHomeDesktop();

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  return (
    <>
      <div className="text-font-black p-10 gap-3 flex flex-col">
        <Box title={translate("calendar", lang)} className="w-full!">
          <div className="flex flex-row gap-3">
            {/* CALENDAR */}
            <div className="flex flex-2/5 justify-center items-center">
              <Calendar
                tileContent={tileContentFuncWrapper}
                prevLabel={
                  <FontAwesomeIcon
                    className="cursor-pointer hover:scale-120 duration-200 ease-in-out"
                    size="lg"
                    icon={faChevronLeft}
                  />
                }
                nextLabel={
                  <FontAwesomeIcon
                    className="cursor-pointer hover:scale-120 duration-200 ease-in-out"
                    size="lg"
                    icon={faChevronRight}
                  />
                }
                prev2Label={null}
                next2Label={null}
                showNeighboringMonth={false}
                navigationLabel={({ label }) => (
                  <div className="p-2 flex flex-row grow text-base font-medium">
                    {label}
                  </div>
                )}
                formatDay={() => ""}
                view="month"
                className="flex flex-col items-center"
              />
            </div>

            {/* DETAILS */}
            <div className="flex-1/5">
              <CalendarDetailsText
                selectedCalendarEntries={selectedCalendarEntries}
                selectedDate={selectedDate}
              />
            </div>
          </div>
        </Box>

        <div className="flex 2xl:flex-row flex-col gap-3">
          {/* CIRCULAR LETTERS */}
          <Box className="min-w-0" title={translate("circularLetters", lang)}>
            <div className="overflow-y-scroll overflow-x-auto scrollbar-thin h-80">
              <Table className="table-fixed">
                {/* TABLE HEADER */}
                <colgroup>
                  {circularLettersColumns.map(({ className }) => (
                    <col className={className} />
                  ))}
                </colgroup>
                <TableHeader className="sticky top-0 bg-canvas-primary z-10">
                  <TableRow>
                    {circularLettersColumns.map((column) => (
                      <TableHeaderCell
                        key={`circular-letters-table-header-${column.key}`}
                      >
                        {column.label}
                      </TableHeaderCell>
                    ))}
                  </TableRow>
                </TableHeader>

                {/* TABLE BODY */}
                <TableBody>
                  {circularLetters?.map((circularLetter, index) => (
                    <TableRow
                      className={
                        index % 2 === 0 ? "bg-white" : "bg-canvas-primary"
                      }
                      key={`circular-letters-table-row-${circularLetter.id}`}
                    >
                      {/* FILE ICON */}
                      <TableCell>
                        <FontAwesomeIcon
                          size="xl"
                          className="text-font-secondary"
                          icon={getFileFAIcon("application/pdf")}
                        />
                      </TableCell>

                      {/* FILENAME */}
                      <TableCell>
                        <Text weight="bold">
                          {showTranslated(circularLetter.name, lang)}
                        </Text>
                      </TableCell>

                      {/* CREATION DATE */}
                      <TableCell>
                        <Text>
                          {formatDateFromLang(
                            new Date(circularLetter.creationDate),
                            lang,
                          )}
                        </Text>
                      </TableCell>

                      {/* DOWNLOAD BUTTON */}
                      <TableCell>
                        <TextButton
                          onClick={() =>
                            onDownloadCircularLetter(circularLetter.id, lang)
                          }
                        >
                          {translate("download", lang)}
                        </TextButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Box>

          {/* MESSAGES */}
          <Box className="min-w-0" title={translate("messages", lang)}>
            <div className="overflow-y-scroll overflow-x-auto scrollbar-thin h-80">
              <Table className="table-fixed">
                <colgroup>
                  {messagesColumns.map(({ className }) => (
                    <col className={className} />
                  ))}
                </colgroup>
                {/* TABLE HEADER */}
                <TableHeader className="sticky top-0 bg-canvas-primary z-10">
                  <TableRow>
                    {messagesColumns.map((column) => (
                      <TableHeaderCell
                        key={`circular-letters-table-header-${column.key}`}
                      >
                        {column.label}
                      </TableHeaderCell>
                    ))}
                  </TableRow>
                </TableHeader>

                {/* TABLE BODY */}
                <TableBody>
                  {messages?.map((message, index) => (
                    <TableRow
                      className={
                        index % 2 === 0 ? "bg-white" : "bg-canvas-secondary"
                      }
                      key={`circular-letters-table-row-${message.id}`}
                    >
                      {/* TITLE */}
                      <TableCell>
                        <Text weight="bold">{message.subject}</Text>
                      </TableCell>

                      {/* CREATION DATE */}
                      <TableCell>
                        <Text>
                          {formatDateFromLang(
                            new Date(message.createdAt),
                            lang,
                          )}
                        </Text>
                      </TableCell>

                      {/* STATUS */}
                      <TableCell>
                        {message.isRead
                          ? translate("readPastTense", lang)
                          : translate("notReadPastTense", lang)}
                      </TableCell>

                      {/* ACTION */}
                      <TableCell>
                        <TextButton
                          onClick={() =>
                            navigate(`/messages/read/${message.id}`)
                          }
                        >
                          {translate("read", lang)}
                        </TextButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Box>
        </div>

        {/* CONTACT PEOPLE */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-row gap-3 overflow-x-auto scrollbar-thin pb-2">
            {/* ACCOUNTANT */}
            {selectedMandate?.buchhalter && (
              <Box
                title={
                  <div className="text-nowrap">
                    {translate("accountant", lang)}
                  </div>
                }
                className="min-w-fit flex-1"
              >
                <Persona
                  size="huge"
                  name={selectedMandate.buchhalter.name}
                  primaryText={selectedMandate.buchhalter.name}
                  secondaryText={selectedMandate.buchhalter.email}
                  quaternaryText={`
                    ${formatPhoneNumber({
                      phoneNumber: selectedMandate.buchhalter.phone ?? "",
                      isMobile: false,
                    })} ⸱ ${formatPhoneNumber({
                      phoneNumber: selectedMandate.buchhalter.cell ?? "",
                      isMobile: true,
                    })}
                  `}
                />
              </Box>
            )}

            {/* ACCOUNTING ADVISOR */}
            {selectedMandate?.beraterBuchhaltung && (
              <Box
                title={
                  <div className="text-nowrap">
                    {translate("accountingAdvisor", lang)}
                  </div>
                }
                className="min-w-fit flex-1"
              >
                <Persona
                  size="huge"
                  name={selectedMandate.beraterBuchhaltung.name}
                  primaryText={selectedMandate.beraterBuchhaltung.name}
                  secondaryText={selectedMandate.beraterBuchhaltung.email}
                  quaternaryText={`
                    ${formatPhoneNumber({
                      phoneNumber:
                        selectedMandate.beraterBuchhaltung.phone ?? "",
                      isMobile: false,
                    })} ⸱ ${formatPhoneNumber({
                      phoneNumber:
                        selectedMandate.beraterBuchhaltung.cell ?? "",
                      isMobile: true,
                    })}
                  `}
                />
              </Box>
            )}

            {/* PAYROLL ACCOUNTANT */}
            {selectedMandate?.lohnBuchhalter && (
              <Box
                title={
                  <div className="text-nowrap">
                    {translate("PAYROLL_ACCOUNTANT", lang)}
                  </div>
                }
                className="min-w-fit flex-1"
              >
                <Persona
                  size="huge"
                  name={selectedMandate.lohnBuchhalter.name}
                  primaryText={selectedMandate.lohnBuchhalter.name}
                  secondaryText={selectedMandate.lohnBuchhalter.email}
                  quaternaryText={`
                    ${formatPhoneNumber({
                      phoneNumber: selectedMandate.lohnBuchhalter.phone ?? "",
                      isMobile: false,
                    })} ⸱ ${formatPhoneNumber({
                      phoneNumber: selectedMandate.lohnBuchhalter.cell ?? "",
                      isMobile: true,
                    })}
                  `}
                />
              </Box>
            )}

            {/* PAYROLL ADVISOR */}
            {selectedMandate?.beraterLoehne && (
              <Box
                title={
                  <div className="text-nowrap">
                    {translate("payrollAdvisor", lang)}
                  </div>
                }
                className="min-w-fit flex-1"
              >
                <Persona
                  size="huge"
                  name={selectedMandate.beraterLoehne.name}
                  primaryText={selectedMandate.beraterLoehne.name}
                  secondaryText={selectedMandate.beraterLoehne.email}
                  quaternaryText={`
                    ${formatPhoneNumber({
                      phoneNumber: selectedMandate.beraterLoehne.phone ?? "",
                      isMobile: false,
                    })} ⸱ ${formatPhoneNumber({
                      phoneNumber: selectedMandate.beraterLoehne.cell ?? "",
                      isMobile: true,
                    })}
                  `}
                />
              </Box>
            )}
          </div>

          {/* EXTERNAL LINKS */}
          {/* STUDIO HR */}
          <div className="flex flex-row justify-center w-full items-center gap-12">
            <a
              href={TEAM_SYSTEM_STUDIO_HR_URL}
              target="_blank"
              className="flex flex-row gap-3 items-center justify-center group"
            >
              <Image className="w-5 h-5" src={tsLogo} />
              <p className="font-semibold text-primary group-hover:underline">
                Studio HR
              </p>
            </a>

            {/* TS DIGITAL */}
            <a
              href={TEAM_SYSTEM_DIGITAL_URL}
              target="_blank"
              className="flex flex-row gap-3 items-center group"
            >
              <Image className="w-5 h-5" src={tsLogo} />
              <p className="font-semibold text-primary group-hover:underline">
                TS Digital
              </p>
            </a>

            {/* FATTURE IN CLOUD */}
            <a
              href={FATTURE_IN_CLOUD_URL}
              target="_blank"
              className="flex flex-row gap-3 items-center group"
            >
              <Image className="w-5 h-5" src={tsLogo} />
              <p className="font-semibold text-primary group-hover:underline">
                Fatture In Cloud
              </p>
            </a>

            {/* YOUTUBE */}
            <a
              href={PSP_YOUTUBE_CHANNEL_URL}
              target="_blank"
              className="flex flex-row gap-2 items-center group"
            >
              <Image className="w-7 h-7" src={ytLogo} />
              <p className="font-semibold text-primary group-hover:underline">
                YouTube
              </p>
            </a>
          </div>

          <div
            className={`
            text-font-secondary
            text-xs
            w-full
            flex
            flex-col
            justify-center
            items-center
            align-middle
            text-center
          `}
          >
            {selectedMandate?.filialeBuchhaltung && (
              <div>
                {`${translate("yourAccountingBranch", lang)} ⸱ ${showTranslated(
                  selectedMandate.filialeBuchhaltung.name,
                  lang,
                )} ⸱ ${showTranslated(selectedMandate.filialeBuchhaltung.address, lang)} ⸱ ${
                  selectedMandate.filialeBuchhaltung.mail
                } ⸱ ${formatPhoneNumber({
                  phoneNumber: selectedMandate.filialeBuchhaltung.phone,
                  isMobile: false,
                })}`}
              </div>
            )}
            {selectedMandate?.filialeLoehne && (
              <div>
                {`${translate("yourPayrollBranch", lang)} ⸱ ${showTranslated(
                  selectedMandate.filialeLoehne.name,
                  lang,
                )} ⸱ ${showTranslated(selectedMandate.filialeLoehne.address, lang)} ⸱ ${
                  selectedMandate.filialeLoehne.mail
                } ⸱ ${formatPhoneNumber({
                  phoneNumber: selectedMandate.filialeLoehne.phone,
                  isMobile: false,
                })}`}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
